import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Users, Gift, Copy, Share2, Check, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/referral');
      return;
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/my-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnTwitter = () => {
    const text = `I'm using The Watchtower to stay informed about UK political accountability. Join me and get access to breaking political news! ${stats?.referral_link}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const progress = stats ? (stats.successful_referrals % 10) / 10 * 100 : 0;

  return (
    <div className="max-w-3xl mx-auto py-12 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
          <Users className="h-10 w-10 text-white" />
        </div>
        <h1 className="text-3xl font-serif font-bold mb-2">Refer Friends, Earn Rewards</h1>
        <p className="text-slate-600">
          Share The Watchtower and earn <strong>3 months of free Premium</strong> for every 10 friends who subscribe!
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <p className="text-4xl font-bold text-purple-600">{stats?.total_referrals || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Total Referrals</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <p className="text-4xl font-bold text-green-600">{stats?.successful_referrals || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Subscribed</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <p className="text-4xl font-bold text-amber-600">{stats?.rewards_earned || 0}</p>
          <p className="text-sm text-slate-500 mt-1">Months Earned</p>
        </div>
      </div>

      {/* Progress to Next Reward */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Gift className="h-6 w-6 text-purple-600" />
            <div>
              <h3 className="font-semibold">Progress to Next Reward</h3>
              <p className="text-sm text-slate-600">
                {stats?.next_reward_at || 10} more subscribers until your next 3 free months
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-600">{stats?.successful_referrals % 10 || 0}/10</p>
          </div>
        </div>
        <div className="w-full bg-purple-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-purple-500 to-indigo-600 h-3 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Referral Link */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold mb-4">Your Referral Link</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={stats?.referral_link || ''}
            readOnly
            className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm"
            data-testid="referral-link-input"
          />
          <Button 
            onClick={copyLink}
            className="px-6"
            data-testid="copy-link-btn"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Your unique code: <strong>{stats?.referral_code}</strong>
        </p>
      </div>

      {/* Share Buttons */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 mb-8">
        <h3 className="font-semibold mb-4">Share on Social Media</h3>
        <div className="flex gap-3">
          <Button 
            onClick={shareOnTwitter}
            className="flex-1 bg-black hover:bg-slate-800"
          >
            <Share2 className="h-4 w-4 mr-2" /> Share on X
          </Button>
          <Button 
            onClick={copyLink}
            variant="outline"
            className="flex-1"
          >
            <Copy className="h-4 w-4 mr-2" /> Copy Link
          </Button>
        </div>
      </div>

      {/* How it Works */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold mb-4">How It Works</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-purple-600">1</span>
            </div>
            <div>
              <p className="font-medium">Share your link</p>
              <p className="text-sm text-slate-600">Send your unique referral link to friends and family</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-purple-600">2</span>
            </div>
            <div>
              <p className="font-medium">Friends subscribe</p>
              <p className="text-sm text-slate-600">When they sign up using your link and subscribe to Premium</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="font-bold text-purple-600">3</span>
            </div>
            <div>
              <p className="font-medium">You earn rewards</p>
              <p className="text-sm text-slate-600">Get 3 months of free Premium for every 10 subscribers!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
